// Public "Solutions" B2B microsite host for the "Institutional" CTAs.
// Derived from the CURRENT frontend origin (prepend the `solutions.` subdomain)
// so it resolves correctly in every environment without per-developer config:
//   www.midash.ai    -> https://solutions.midash.ai
//   midash.ai        -> https://solutions.midash.ai
//   c4.midash.ai     -> https://solutions.c4.midash.ai
//   localhost:5351   -> http://solutions.localhost:5351   (browsers resolve *.localhost)
// Set VITE_SOLUTIONS_URL to override explicitly (e.g. a bespoke dev vhost).
function deriveSolutionsUrl(): string {
  const override = import.meta.env.VITE_SOLUTIONS_URL;
  if (override) return override;
  // SSR / prerender (no window): fall back to the canonical production URL.
  if (typeof window === "undefined") return "https://solutions.midash.ai";
  const { protocol, hostname, port } = window.location;
  // A bare IP host can't take a subdomain — use the canonical production URL.
  if (hostname === "" || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
    return "https://solutions.midash.ai";
  }
  const base = hostname.replace(/^www\./, "");
  return `${protocol}//solutions.${base}${port ? `:${port}` : ""}`;
}

export const SOLUTIONS_URL = deriveSolutionsUrl();

// Institutional CTAs use this: the ?ref=platform marker lets the Solutions
// landing reveal a "Back to MiDash" button for users who came from the B2C site.
export const SOLUTIONS_URL_FROM_PLATFORM = `${SOLUTIONS_URL}/?ref=platform`;

/**
 * Destination for every "Build Your Trading Agent" CTA.
 *
 * MiDash is at full capacity — new-user registration is closed (the auth
 * service rejects it server-side, and /auth/register no longer routes).
 * Every CTA that used to open the signup/trial funnel now points here
 * instead, so marketing traffic converts into custom-agent enquiries
 * rather than dead-ending on a disabled register page.
 *
 * `source` is appended as ?ref= so the Solutions site can attribute the
 * visit (it already reads ?ref for its "Back to MiDash" affordance, and
 * kit/track.js persists first-touch attribution from the query string).
 * Keep the values short and stable — they show up in lead emails.
 */
export function buildAgentUrl(source: string): string {
  return `${SOLUTIONS_URL}/?ref=${encodeURIComponent(source)}`;
}

/**
 * Imperative navigation for click handlers that previously called
 * navigate('/auth/register'). Uses a full page load (not react-router)
 * because the destination is a different host.
 */
export function goToBuildAgent(source: string): void {
  window.location.href = buildAgentUrl(source);
}
